/**
 * positivize.js — Helper to rewrite negative/cautionary phrasing into a
 * warmer, more inviting style for the Homeyoursoul blog.
 *
 * Rules are evaluated in order; longer phrases come first so that we don't
 * double-replace fragments. All matches are case-insensitive but preserve
 * the original sentence casing where the replacement starts the sentence.
 */

// Ordered list of [pattern, replacement] tuples. Patterns are regular
// expressions; replacements can be strings or functions that receive the
// match groups (standard RegExp.replace API).
const RULES = [
  // Whole-sentence rewrites for the most common negative openers
  [/Was du dir bewusst NICHT kaufen solltest/gi, 'Worauf du beim Einrichten achten kannst'],
  [/Was du nicht (kaufen|tun|machen) solltest/gi, 'Worauf du achten kannst'],
  [/Du musst nicht\b/gi, 'Du darfst'],
  [/Du musst weder/gi, 'Du darfst'],
  [/Du musst auch nicht/gi, 'Du darfst auch'],
  [/Vermeide es,/gi, 'Achte darauf,'],
  [/Vermeide\b/gi, 'Wähle bewusst'],

  // Soften "Nachteile" framing
  [/\b(die )?Nachteile\b/gi, 'was zu bedenken ist'],
  [/\bNachteil\b/gi, 'Punkt zum Überlegen'],

  // Negativity around effort / failure
  [/erschöpft, frustriert/gi, 'mit gemischtem Gefühl'],
  [/Stress-Auslöser/gi, 'kleines Hindernis'],
  [/jede Stimmung killen/gi, 'die Stimmung weniger einladend machen'],
  [/killen\b/gi, 'dämpfen'],
  [/anonym wie ein Möbelhaus-Showroom/gi, 'wie ein Showroom statt deinem Zuhause'],
  [/wirken steril/gi, 'wirken weniger persönlich'],

  // "Lass die Finger weg" / harsh prohibitions
  [/Lass die Finger (weg|davon)/gi, 'Schau es dir bewusst an'],
  [/Auf keinen Fall/gi, 'Lieber'],
  [/auf gar keinen Fall/gi, 'lieber nicht'],

  // Generic warnings turned into invitations
  [/Falsche Haltung\?/gi, 'Mit der richtigen Haltung'],
  [/\bFalsch\.\s/g, 'Es geht auch anders. '],
  [/Ausrede\b/gi, 'Vorhaben'],
  [/Hemmschwelle/gi, 'erster Schritt'],

  // "Verhindert / hat keine" rewrites
  [/das verhindert (\w+)/gi, 'das schont $1'],
  [/verhindert Haarbruch/gi, 'schont das Haar'],
  [/verhindert Brüche/gi, 'schont die Haare'],

  // "Risiko" / "Gefahr"
  [/das Risiko der\b/gi, 'die Möglichkeit zur'],

  // "Du wirst nicht"
  [/du wirst nicht\b/gi, 'du wirst kaum'],

  // "Mehr braucht es nicht" - keep this one, sounds friendly
  // (No replacement)
];

/**
 * Apply all rules to a single string.
 * Returns the rewritten string and the number of replacements made.
 */
export function positivizeString(input) {
  if (typeof input !== 'string' || !input) return { text: input || '', changes: 0 };
  let result = input;
  let changes = 0;
  for (const [pattern, replacement] of RULES) {
    const before = result;
    result = result.replace(pattern, replacement);
    if (result !== before) {
      // Count match occurrences
      const matches = before.match(new RegExp(pattern.source, pattern.flags));
      changes += matches ? matches.length : 1;
    }
  }
  return { text: result, changes };
}

/**
 * Apply rules across an array of editor blocks (text-bearing types only).
 */
export function positivizeBlocks(blocks) {
  let totalChanges = 0;
  const newBlocks = blocks.map((b) => {
    if (b.type === 'image') return b; // skip images
    const { text, changes } = positivizeString(b.content);
    totalChanges += changes;
    return changes > 0 ? { ...b, content: text } : b;
  });
  return { blocks: newBlocks, changes: totalChanges };
}

/**
 * Highlight-friendly scan: returns an array of {block_id, original, suggestion}
 * for any block that has at least one match. Useful for UI previews before
 * applying changes.
 */
export function scanForNegativity(blocks) {
  const findings = [];
  for (const b of blocks) {
    if (b.type === 'image') continue;
    const { text, changes } = positivizeString(b.content);
    if (changes > 0) {
      findings.push({
        block_id: b.id,
        type: b.type,
        original: b.content,
        suggestion: text,
        changes,
      });
    }
  }
  return findings;
}
